import type { AuthUser } from '@hemmafixa/shared';
import type { Language } from '@hemmafixa/ui';
import { useCallback, useEffect, useRef } from 'react';
import { useI18n } from '@/i18n/context';
import { useSidebarCollapsed } from '@/lib/preferences';
import { useUpdatePreferences } from './api';

/**
 * Language and sidebar state for a signed-in user. The values saved on the account win when the user loads
 * (so they follow the person to a new device); every change is applied at once and then saved. The browser
 * keeps a copy too, so the next visit starts right before the user has loaded.
 */
export function useAccountPreferences(user: AuthUser) {
  const { language, setLanguage } = useI18n();
  const sidebar = useSidebarCollapsed();
  const save = useUpdatePreferences();
  const { mutate } = save;

  // Apply a saved value once per value, not on every render: a change made here waits for the server's answer
  // without being undone by the older value still in the session
  const appliedLocale = useRef<string | null>(null);
  useEffect(() => {
    const key = `${user.id}:${user.locale}`;
    if (appliedLocale.current === key) return;
    appliedLocale.current = key;
    if (user.locale !== language) setLanguage(user.locale);
  }, [user.id, user.locale, language, setLanguage]);

  const appliedSidebar = useRef<string | null>(null);
  const { collapsed, setCollapsed } = sidebar;
  useEffect(() => {
    const key = `${user.id}:${user.sidebarCollapsed}`;
    if (appliedSidebar.current === key) return;
    appliedSidebar.current = key;
    if (user.sidebarCollapsed !== collapsed) setCollapsed(user.sidebarCollapsed);
  }, [user.id, user.sidebarCollapsed, collapsed, setCollapsed]);

  const changeLanguage = useCallback(
    (next: Language) => {
      setLanguage(next);
      mutate({ locale: next });
    },
    [setLanguage, mutate],
  );

  const changeCollapsed = useCallback(
    (next: boolean) => {
      setCollapsed(next);
      mutate({ sidebarCollapsed: next });
    },
    [setCollapsed, mutate],
  );

  return { language, setLanguage: changeLanguage, collapsed, setCollapsed: changeCollapsed };
}
